import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { DICTIONARIES, isLoaded, loadLang } from "../i18n";

export const LANGS = ["zh", "en", "nl", "fr", "de", "it", "es", "pt", "cs", "pl", "hu"] as const;
export type Lang = (typeof LANGS)[number];
export type LangPref = Lang | "auto";
export const LANG_NAMES: Record<Lang, string> = {
  zh: "中文", en: "English", nl: "Nederlands", fr: "Français", de: "Deutsch", it: "Italiano",
  es: "Español", pt: "Português", cs: "Čeština", pl: "Polski", hu: "Magyar",
};
/** The languages written in the namespace files themselves; the others come as one file each (locales/). */
export type BaseLang = "zh" | "en" | "nl";
/** For content that only exists in Chinese, English and Dutch (recipes, some fixed texts): the closest one. */
export const baseLang = (l: Lang): BaseLang => (l === "zh" || l === "nl" ? l : "en");

/** A namespace file: the same flat keys for every language. English is the fallback. */
export type Dict = { zh: Record<string, string>; en: Record<string, string>; nl: Record<string, string> };
export { loadLang };

export function resolveLang(pref: LangPref, device: readonly string[] = navigator.languages ?? []): Lang {
  if (pref !== "auto") return pref;
  for (const tag of device) {
    const base = tag.toLowerCase().split("-")[0];
    if ((LANGS as readonly string[]).includes(base)) return base as Lang;
  }
  return "en";
}

export type T = (key: string, params?: Record<string, string | number>) => string;

export function makeT(lang: Lang): T {
  return (key, params) => {
    let s = DICTIONARIES[lang][key] ?? DICTIONARIES.en[key];
    if (s === undefined) {
      if (import.meta.env.DEV) console.warn(`[i18n] missing key ${key}`);
      s = key;
    }
    if (params) s = s.replace(/\{(\w+)\}/g, (m, k) => (params[k] !== undefined ? String(params[k]) : m));
    return s;
  };
}

interface I18nValue {
  lang: Lang;
  pref: LangPref;
  setPref: (p: LangPref) => void;
  t: T;
}

const Ctx = createContext<I18nValue | null>(null);
const STORAGE_KEY = "nutricart.language";

/** NutriCart opens in Chinese unless the person picked another language (or "same as my phone"). */
export const DEFAULT_LANG: Lang = "zh";

function readStoredPref(): LangPref {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "auto" || (LANGS as readonly string[]).includes(v ?? "")) return v as LangPref;
  } catch {}
  return DEFAULT_LANG;
}

/** Before the first render: fetch the stored language's texts when it isn't built in. */
export const preloadStoredLang = () => loadLang(resolveLang(readStoredPref()));

export function I18nProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<LangPref>(readStoredPref);
  const [deviceTick, setDeviceTick] = useState(0);
  useEffect(() => {
    const on = () => setDeviceTick((n) => n + 1);
    window.addEventListener("languagechange", on);
    return () => window.removeEventListener("languagechange", on);
  }, []);
  const wanted = useMemo(() => resolveLang(pref), [pref, deviceTick]);
  // A language that comes as its own file shows once it has arrived (the previous language until then).
  const [lang, setLang] = useState<Lang>(() => (isLoaded(wanted) ? wanted : "en"));
  useEffect(() => {
    let live = true;
    const load = () =>
      loadLang(wanted)
        .then(() => live && setLang(isLoaded(wanted) ? wanted : "en"))
        .catch(() => {});
    load();
    // Offline when the language was first wanted: it is fetched again as soon as the phone is back online.
    window.addEventListener("online", load);
    return () => {
      live = false;
      window.removeEventListener("online", load);
    };
  }, [wanted]);
  useEffect(() => {
    document.documentElement.lang = lang === "zh" ? "zh-CN" : lang;
  }, [lang]);
  const value = useMemo<I18nValue>(
    () => ({
      lang,
      pref,
      t: makeT(lang),
      setPref(p) {
        setPrefState(p);
        try {
          localStorage.setItem(STORAGE_KEY, p);
        } catch {}
      },
    }),
    [lang, pref],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useI18n outside provider");
  return v;
}
